import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'aikon.pageSize.';

/**
 * Lee el tamaño guardado, si es uno de los que la pantalla ofrece.
 *
 * Se valida contra las opciones a propósito: el valor vive en el navegador del
 * usuario y puede quedar obsoleto —o editado a mano— y una pantalla pidiendo
 * 5000 renglones sería rechazada por el backend, que topa en 100.
 */
const readStored = (key: string, options: number[], fallback: number): number => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return fallback;

    const parsed = Number(raw);

    return options.includes(parsed) ? parsed : fallback;
  } catch {
    // Modo privado de Safari y navegadores con almacenamiento bloqueado lanzan
    // al leer. Sin preferencia guardada, el valor por omisión sirve igual.
    return fallback;
  }
};

/**
 * Tamaño de página recordado entre visitas, por pantalla.
 *
 * Cada pantalla usa su propia clave: el número de renglones que se quiere ver
 * en Noticias no tiene por qué ser el de Miembros.
 *
 * @param key - Identificador de la pantalla, p. ej. `'courseMembers'`.
 * @param fallback - Tamaño a usar cuando no hay nada guardado.
 * @param options - Tamaños válidos. Un valor guardado fuera de esta lista se ignora.
 * @returns `[pageSize, setPageSize]`
 */
const usePageSize = (
  key: string,
  fallback: number,
  options: number[] = [10, 25, 50, 100]
): [number, (next: number) => void] => {
  const [pageSize, setPageSizeState] = useState(() => readStored(key, options, fallback));

  const setPageSize = useCallback(
    (next: number) => {
      setPageSizeState(next);

      try {
        window.localStorage.setItem(STORAGE_PREFIX + key, String(next));
      } catch {
        // Que no se pueda recordar no es motivo para no cambiar de tamaño.
      }
    },
    [key]
  );

  return [pageSize, setPageSize];
};

export default usePageSize;
